import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Card,
  CardContent,
  Divider,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { formatDistanceToNow, format } from 'date-fns';
import toast from 'react-hot-toast';
import axios from 'axios';

import { getUserDetails } from '../utils';
import EmptyState from '../components/empty-state';

function stringToColor(string) {
  let hash = 0;
  let i;
  /* eslint-disable no-bitwise */
  for (i = 0; i < string?.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */
  return color;
}

const AppNotifications = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userDetails = getUserDetails();
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pagination, setPagination] = useState(null);
  const limit = 50;

  // Ref for the bottom sentinel element (IntersectionObserver target)
  const bottomSentinelRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const paginationRef = useRef(null);
  const currentPageRef = useRef(1);

  // Keep refs in sync with state
  useEffect(() => { isLoadingMoreRef.current = isLoadingMore; }, [isLoadingMore]);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

  // Load initial notifications
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/notification/get-notifications`,
          {
            headers: { Authorization: `Bearer ${userDetails?.token}` },
            params: { page: 1, limit },
          }
        );
        if (response.data?.data) {
          setNotifications(response.data.data.notifications || []);
          setPagination(response.data.data.pagination);
        }
      } catch (error) {
        console.error('Error loading notifications:', error);
        toast.error('Failed to load notifications');
      } finally {
        setIsLoading(false);
      }
    };

    if (userDetails?.token) {
      loadNotifications();
    }
  }, [userDetails?.token]);

  // Load more notifications (called by IntersectionObserver)
  const loadMoreNotifications = useCallback(async () => {
    if (isLoadingMoreRef.current || !paginationRef.current?.hasNextPage) return;

    try {
      setIsLoadingMore(true);
      const nextPage = currentPageRef.current + 1;
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/notification/get-notifications`,
        {
          headers: { Authorization: `Bearer ${userDetails?.token}` },
          params: { page: nextPage, limit },
        }
      );
      if (response.data?.data) {
        setNotifications((prev) => [...prev, ...(response.data.data.notifications || [])]);
        setPagination(response.data.data.pagination);
        setCurrentPage(nextPage);
      }
    } catch (error) {
      console.error('Error loading more notifications:', error);
      toast.error('Failed to load more notifications');
    } finally {
      setIsLoadingMore(false);
    }
  }, [userDetails?.token]);

  // IntersectionObserver: when bottom sentinel becomes visible → load more
  useEffect(() => {
    const sentinel = bottomSentinelRef.current;
    if (!sentinel) {
      return () => {};
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMoreNotifications();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMoreNotifications]);

  useEffect(() => {
    // If navigating from notification item, scroll to that notification
    if (location.state?.notificationId) {
      const notification = notifications.find(n => n._id === location.state.notificationId);
      if (notification) {
        setSelectedNotification(notification);
        setTimeout(() => {
          const element = document.getElementById(`notification-${notification._id}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
  }, [notifications, location.state]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Helmet>
        <title>Edifying Teachers | Notifications</title>
      </Helmet>

      <Container maxWidth="md" sx={{ padding: '0 !important' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                bgcolor: 'rgba(142, 113, 173, 0.08)',
                '&:hover': {
                  bgcolor: 'rgba(142, 113, 173, 0.12)',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography
                variant="h3"
                sx={{ color: 'text.titles', lineHeight: 1.2, mb: unreadCount > 0 ? 0.5 : 0 }}
              >
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {unreadCount} unread {unreadCount === 1 ? 'notification' : 'notifications'}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Notifications List */}
        {notifications.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {notifications.map((notification) => {
              const user = notification?.from;
              const userName = user ? `${user.fname || ''} ${user.lname || ''}`.trim() : 'System';
              const userImage = user?.image;
              const userInitials = user ? `${user.fname?.[0] || ''}${user.lname?.[0] || ''}`.toUpperCase() : 'S';
              const timeAgo = notification?.timestamp
                ? formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })
                : '';
              const fullDate = notification?.timestamp
                ? format(new Date(notification.timestamp), 'PPpp')
                : '';

              return (
                <Card
                  key={notification._id}
                  id={`notification-${notification._id}`}
                  sx={{
                    borderRadius: 3,
                    boxShadow: selectedNotification?._id === notification._id
                      ? '0px 8px 24px rgba(142, 113, 173, 0.25)'
                      : '0px 4px 20px rgba(0, 0, 0, 0.08)',
                    border: selectedNotification?._id === notification._id
                      ? '2px solid'
                      : '1px solid',
                    borderColor: selectedNotification?._id === notification._id
                      ? 'primary.main'
                      : notification?.isRead
                        ? 'divider'
                        : 'primary.light',
                    backgroundColor: notification?.isRead
                      ? 'background.paper'
                      : 'rgba(142, 113, 173, 0.04)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0px 8px 24px rgba(142, 113, 173, 0.15)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2.5 }}>
                      {/* Avatar */}
                      <Avatar
                        src={userImage}
                        alt={userName}
                        sx={{
                          width: 64,
                          height: 64,
                          bgcolor: user ? stringToColor(userName) : 'primary.main',
                          border: '3px solid',
                          borderColor: notification?.isRead ? 'divider' : 'primary.main',
                          flexShrink: 0,
                        }}
                      >
                        {!userImage && (
                          user ? (
                            userInitials
                          ) : (
                            <AccountCircleIcon sx={{ fontSize: 40, color: 'white' }} />
                          )
                        )}
                      </Avatar>

                      {/* Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: notification?.isRead ? 600 : 700,
                                color: 'text.primary',
                                mb: 1,
                                fontSize: '18px',
                              }}
                            >
                              {notification?.title || 'Notification'}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '15px',
                                lineHeight: 1.6,
                                mb: 1.5,
                              }}
                            >
                              {notification?.message}
                            </Typography>
                          </Box>
                          {!notification?.isRead && (
                            <Chip
                              label="New"
                              size="small"
                              sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '11px',
                                height: 24,
                                ml: 1,
                              }}
                            />
                          )}
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Footer */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            {user && (
                              <>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'text.primary',
                                    fontWeight: 500,
                                    fontSize: '13px',
                                  }}
                                >
                                  From: <span style={{ color: 'primary.main', fontWeight: 600 }}>{userName}</span>
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'text.disabled',
                                    fontSize: '12px',
                                  }}
                                >
                                  •
                                </Typography>
                              </>
                            )}
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.disabled',
                                fontSize: '12px',
                              }}
                              title={fullDate}
                            >
                              {timeAgo}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}

            {/* Bottom sentinel – IntersectionObserver triggers loadMoreNotifications */}
            <Box ref={bottomSentinelRef} sx={{ height: 1 }} />

            {/* Loading spinner while fetching next page */}
            {isLoadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3, gap: 1.5 }}>
                <CircularProgress size={22} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Loading more notifications...
                </Typography>
              </Box>
            )}

            {/* End-of-list indicator */}
            {!pagination?.hasNextPage && notifications.length > 0 && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  You've reached the end · {notifications.length} notification{notifications.length !== 1 ? 's' : ''} total
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <EmptyState
            icon={NotificationsIcon}
            title="No Notifications"
            description="You don't have any notifications yet. When you receive notifications, they'll appear here."
            iconClassName="notification-icon"
            dotsClassName="notification-dots"
          />
        )}
      </Container>
    </Box>
  );
};

export default AppNotifications;
